define((require) => {
    "use strict";

    const AddUserView         = require("ui/settings/manageAccount/AddUserView");
    const ConfirmationManager = require("services/ConfirmationManager");
    const {DateUtil}          = require("shared/util/DateUtil");
    const EditBehavior        = require("ui/behaviors/EditBehavior");
    const EditorEvents        = require("shared/services/EditorEvents");
    const Lightbox            = require("shared/ui/common/Lightbox");
    const LoggedIn            = require("shared/security/LoggedIn");
    const LoggedInUserState   = require("shared/ui/user/LoggedInUserState");
    const ManageUsersHelper   = require("ui/settings/manageAccount/ManageUsersHelper");
    const ManageUsersItemView = require("ui/settings/manageAccount/ManageUsersItemView");
    const Marionette          = require("marionette");
    const NotificationBar     = require("editor/ui/notificationbar/NotificationBar");
    const Permissions         = require("shared/security/Permissions");
    const SequentialButton    = require("util/SequentialButton");
    const template            = require("ui/settings/manageAccount/ManageAccountCompositeView.tpl");
    const User                = require("editor/ui/user/User");

    /**
     * The manage account view.
     *
     * @class ManageAccountView
     * @constructor
     *
     * @cfg {User} model The principal, current logged in user model to display.
     * @cfg {UserCollection} collection The user collection to display.
     */
    return Marionette.CompositeView.extend({

        /**
         * @private
         * @type {String}
         * The display name of the first found unconfirmed user, if any. The user is chosen at random, to
         * be able to display a message to the manager about unconfirmed users. Otherwise null.
         */
        _unconfirmed: null,

        _startDate: null,

        _endDate: null,

        template,

        className: "manage-account-composite-view",

        ui: {
            currentUserPermissionsSelect:  ".current-user-permissions-select",
            currentUserPermissionsDisplay: ".current-user-permissions-display",
            tabMenuItem: ".tab",
            users: ".users",
            editUser: ".info-user .edit-user",
            addUser: ".btn-add-user"
        },

        behaviors: {
            editBehavior: {
                behaviorClass: EditBehavior,
                container: ".editable-container",
                editFields: [{
                    input: ".current-user-name",
                    model: "name",
                    save(value) {
                        this.model.set("name", value);

                        return this.model.saveLater().done(() => {
                            const eventProperties = {
                                userId: this.model.get("id"),
                                name: this.model.get("name"),
                                username: this.model.get("username"),
                                triggerOrigin: "EditUserNameInputField",
                                updatedProperty: "name",
                                updatedValue: value
                            };

                            EditorEvents.trigger(EditorEvents.constants.UPDATE_USER, eventProperties);
                        });
                    }
                }]
            }
        },

        itemView: ManageUsersItemView,

        itemViewOptions: null,

        itemViewContainer: "#users",

        bindings: {
            ".current-user-name": {
                observe: "name",
                onGet(name) {
                    let s = name || this.model.get("username");

                    if (ConfirmationManager.confirmationRequired(this.model)) {
                        s += " (Unconfirmed)";
                    }

                    return s;
                },
                // We need to use updateMethod "html" and escape true, because we are using a tag contenteditable
                updateMethod: "html",
                escape: true
            },
            ".current-accessed-at": {
                observe: "accessedAt",
                onGet(date) {
                    return `Last login: ${date ? DateUtil.formatYYYYMMDDHHmm(date) : "Never"}`;
                }
            },
            ".current-user-email": "username",
            ".current-user-permissions-display": {
                observe: "role",
                onGet(role) {
                    return ManageUsersHelper.createRoleLabel(role.name);
                }
            }
        },

        events: {
            "click @ui.addUser": "_addUserClickHandler"
        },

        templateHelpers() {
            const company = LoggedInUserState.get("company");

            return {
                unconfirmed:      this._unconfirmed,
                cannotAdd:        Permissions.Roles.MANAGER.cannot(),
                adLogin:          LoggedIn.ad(),
                companyName:      company.get("name"),
                salesForceId:     company.get("salesForceId"),
                hasMultipleUsers: this.collection && this.collection.size() > 1,
                startDate:        this._startDate,
                endDate:          this._endDate
            };
        },

        initialize() {
            const principal = LoggedIn.principal();

            // Generate list of roles "principal" can assign to other users from the same company...
            const roles = [];
            // Do not bother with only Spectator role, since nothing to change then - looks better with just
            // the text then...
            if (Permissions.Roles.SPECTATOR.not(principal)) {
                Permissions.Roles.each((role) => {
                    if (role.can(principal)) {
                        roles.push(role.name);
                    }
                });
            }

            this._unconfirmed = this._findUnconfirmedUser(principal);

            this.listenTo(this.collection, "change:role", () => {
                const unconfirmed = this._findUnconfirmedUser(principal);

                if (unconfirmed !== this._unconfirmed) {
                    this._unconfirmed = unconfirmed;
                    this.render();
                }
            });

            this.itemViewOptions = {
                principal,
                roles
            };
        },
        _addUserClickHandler(event) {
            event.preventDefault();
            // FIXME Would be nice to get this logic in a ManageUsersViewController, so far no luck with it
            this.addNewUser();
        },

        /**
         * @private
         * Toggles active class on tabs and content.
         *
         * @param {String} tabName The tabName that should be activated.
         */
        _selectTab(tabName) {
            this.ui.users.toggleClass("active", tabName === "users");
        },

        /**
         * Function for creating a new user, only Manager can do that.
         */
        addNewUser() {
            if (Permissions.Roles.MANAGER.cannot()) {
                return;
            }

            const notifyUserCreation = (user) => {
                const userModel = new User(user);
                let msg = `${userModel.distinguishedName()} has been added as a user to your company.`;

                if (Permissions.Roles.SPECTATOR.not(userModel)) {
                    msg += ` A confirmation email has been sent to ${userModel.get("username")}.`;
                }

                this.collection.add(userModel);
                NotificationBar.open(msg, {queue: false, closeDelay: 7500});
                box.close();
            };

            const addUserOptions = {
                model:  new User(),
                emails: this.collection.map((user) => {
                    return user.get("username");
                }),
                cancelHandler() {
                    box.close();
                },
                okHandler(addUser) {
                    addUser()
                        .done(notifyUserCreation)
                        .fail((state) => {
                            if (state !== SequentialButton.REJECT) { // validation error, e.g. invalid email
                                box.close();
                            }
                        });
                }
            };

            const addUserView = new AddUserView(addUserOptions).render();

            const lightboxOptions = {
                css: {
                    width: 640
                },
                editorEventProperties: {
                    modalType: "AddUser",
                    username: this.model.get("username"),
                    triggerOrigin: "AddUserModal"
                }
            };

            const box = new Lightbox(addUserView.el, lightboxOptions);
            box.open();
        },

        _findUnconfirmedUser(principal) {
            let unconfirmed = null;

            if (ConfirmationManager.confirmationRequired(principal.get("user"))) {
                unconfirmed = "your account";
            } else {
                this.collection.each((user) => {
                    if (!unconfirmed && ConfirmationManager.confirmationRequired(user)) {
                        unconfirmed = user.get("displayName"); // TODO: how do I break out of this loop early?
                    }
                });
            }

            return unconfirmed;
        },

        onRender() {
            if (Permissions.Roles.MANAGER.cannot()) {
                this.ui.currentUserPermissionsSelect.hide();
            }
        }

    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/settings/manageAccount/ManageAccountCompositeView.js